import { useState } from 'react'
import { Plus, Star } from 'lucide-react'
import { BereichCard } from '../dashboard/bereich-card.jsx'
import { CreateBereichSheet } from './create-bereich-sheet.jsx'
import { useFavorites } from '../hooks/use-favorites.js'

export function BereicheTab({ userStores, landingData, onSelectStore, onCreateStore, onJoinStore, dark }) {
  const { isFav, toggle } = useFavorites()
  const [createOpen, setCreateOpen] = useState(false)

  const stores = (userStores || []).slice().sort((a, b) =>
    (a.name || '').localeCompare(b.name || '', 'de')
  )

  // Helfer-Stores werden NICHT separiert — sie sind "seine" Bereiche und nur per
  // Card-Tag/Glow unterschieden. Nur eigene Stores sind favorisierbar.
  const favoritable = stores.filter(s => s.role !== 'fulfillment')
  const favs = favoritable.filter(s => isFav(s.id))
  const weitere = stores.filter(s => !(s.role !== 'fulfillment' && isFav(s.id)))

  const useSections = stores.length >= 4

  return (
    <div className="px-[18px]" style={{ paddingBottom: 'calc(72px + env(safe-area-inset-bottom, 0px))' }}>
      {/* Header */}
      <div className="flex items-center justify-between py-4">
        <h2 className="text-[22px] font-medium">Bereiche</h2>
        <button
          onClick={() => setCreateOpen(true)}
          className="w-11 h-11 rounded-full bg-[var(--fill-tertiary)] flex items-center justify-center active:scale-95 transition-transform"
          aria-label="Neuen Bereich anlegen"
        >
          <Plus size={20} className="text-[var(--label-secondary)]" />
        </button>
      </div>

      {/* Banner bei 0 Favoriten */}
      {favs.length === 0 && favoritable.length > 0 && (
        <div className="bg-[var(--bg-L2)] rounded-xl p-4 flex items-center gap-3 mb-4">
          <Star size={18} style={{ color: 'var(--accent-yellow)' }} />
          <p className="text-[13px] text-[var(--label-secondary)]">
            Markiere Bereiche mit Stern, um sie aufs Dashboard zu holen.
          </p>
        </div>
      )}

      {/* Flat list (< 4 stores) — Helfer gemerged, kein Favoriten-Stern für Helfer */}
      {!useSections && (
        <>
          {stores.map(s => {
            const canFav = s.role !== 'fulfillment'
            return (
              <BereichCard
                key={s.id}
                store={s}
                storeData={landingData?.byStore?.[s.id]}
                isFavorite={canFav ? isFav(s.id) : undefined}
                onToggleFavorite={canFav ? toggle : undefined}
                onClick={() => onSelectStore?.(s)}
              />
            )
          })}
        </>
      )}

      {/* Sectioned list (>= 4 stores) */}
      {useSections && (
        <>
          {favs.length > 0 && (
            <>
              <SectionLabel>Favoriten</SectionLabel>
              {favs.map(s => (
                <BereichCard
                  key={s.id}
                  store={s}
                  storeData={landingData?.byStore?.[s.id]}
                  isFavorite
                  onToggleFavorite={toggle}
                  onClick={() => onSelectStore?.(s)}
                />
              ))}
            </>
          )}

          {weitere.length > 0 && (
            <>
              <SectionLabel>{favs.length > 0 ? 'Weitere' : 'Meine Bereiche'}</SectionLabel>
              {weitere.map(s => {
                const canFav = s.role !== 'fulfillment'
                return (
                  <BereichCard
                    key={s.id}
                    store={s}
                    storeData={landingData?.byStore?.[s.id]}
                    isFavorite={canFav ? isFav(s.id) : undefined}
                    onToggleFavorite={canFav ? toggle : undefined}
                    onClick={() => onSelectStore?.(s)}
                  />
                )
              })}
            </>
          )}
        </>
      )}

      {stores.length === 0 && (
        <div className="text-center py-12">
          <p className="text-[15px] text-[var(--label-secondary)]">Noch keine Bereiche</p>
        </div>
      )}

      <CreateBereichSheet
        open={createOpen}
        onOpenChange={setCreateOpen}
        userStores={userStores}
        onCreateStore={onCreateStore}
        onJoinStore={onJoinStore}
        dark={dark}
      />
    </div>
  )
}

function SectionLabel({ children }) {
  return (
    <div className="text-[11px] font-medium text-[var(--label-secondary)] tracking-wider uppercase mb-2 mt-5">
      {children}
    </div>
  )
}
